/** DriverPace — pace analysis table showing median best lap per track/stage */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { rpc } from '@/api/client';
import { useI18n } from '@/context/I18nContext';
import { useRouter } from '@/context/RouterContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { DriverView } from './DriverView';
import { formatLapTime } from './ResultsTable';
import type { GetDriverRacePaceResponse, DriverTrackPace, PaceRef } from '@/types/telemetry';

/** Category order — Micro through DD2 Master. */
const CATEGORY_ORDER = ['Micro', 'Mini', 'Junior', 'Club', 'Max', 'Master', 'DD2', 'DD2 Master'];

/** Deduplicate and sort categories using the canonical order. */
function sortAndDedup(categories: string[]): string[] {
  const unique = [...new Set(categories)];
  return unique.sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    if (ai >= 0 && bi >= 0) return ai - bi;
    if (ai >= 0) return -1;
    if (bi >= 0) return 1;
    return a.localeCompare(b);
  });
}

/** Pick the default category: the latest (highest order) one. */
function pickDefaultCategory(categories: string[]): string | null {
  const sorted = sortAndDedup(categories);
  return sorted.length > 0 ? sorted[sorted.length - 1] : null;
}

interface DriverPaceProps {
  driverId: string;
}

export function DriverPace({ driverId }: DriverPaceProps) {
  const { t } = useI18n();

  const [data, setData] = useState<GetDriverRacePaceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Load initial data (no category filter) to get the list of categories
  const loadCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await rpc<GetDriverRacePaceResponse>('GetDriverRacePace', { driver_id: driverId });
      setData(res);
      // Default to latest category
      const defaultCat = pickDefaultCategory(res.categories ?? []);
      setActiveCategory(defaultCat);
    } catch (e: any) {
      setError(e.message || t('races.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [driverId, t]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  // Load pace data for the selected category
  const loadPace = useCallback(async (category: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await rpc<GetDriverRacePaceResponse>('GetDriverRacePace', { driver_id: driverId, category_name: category });
      setData(prev => prev ? { ...prev, tracks: res.tracks } : res);
    } catch (e: any) {
      setError(e.message || t('races.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [driverId, t]);

  useEffect(() => {
    if (activeCategory) loadPace(activeCategory);
  }, [activeCategory, loadPace]);

  const categories = useMemo(() => sortAndDedup(data?.categories ?? []), [data?.categories]);

  return (
    <>
      <DriverView driverId={driverId} activeTab="pace" />

      {/* Category chips */}
      {categories.length > 0 && (
        <div className="flex items-center gap-1 mb-6">
          {categories.map(cat => {
            const isActive = cat === activeCategory;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                  isActive
                    ? 'text-accent bg-accent/10'
                    : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>
      )}

      {/* Pace subtitle */}
      <p className="text-xs text-text-muted mb-4">{t('races.paceDescription')}</p>

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState message={error} />
      ) : !data?.tracks?.length ? (
        <div className="card p-8 text-center text-text-muted text-sm">{t('races.paceNoData')}</div>
      ) : (
        <PaceTable tracks={data.tracks} driverId={driverId} />
      )}
    </>
  );
}

function PaceTable({ tracks, driverId }: { tracks: DriverTrackPace[]; driverId: string }) {
  const { t } = useI18n();
  const { navigate } = useRouter();

  // Sort tracks alphabetically
  const sorted = useMemo(() => [...tracks].sort((a, b) => a.trackName.localeCompare(b.trackName)), [tracks]);

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[520px] border-collapse">
          <thead>
            <tr className="divider">
              <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('races.track')}</th>
              <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('races.sessionType.qualifying')}</th>
              <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('races.sessionType.heat-1')}</th>
              <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('races.sessionType.heat-2')}</th>
              <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('races.sessionType.final')}</th>
              <th className="text-center py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('races.events')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((track, index) => (
              <tr
                key={track.trackName}
                className={`hover:bg-bg-hover transition-colors ${index < sorted.length - 1 ? 'divider' : ''}`}
              >
                <td className="py-3 px-4">
                  <span className="font-semibold text-text-primary text-sm">{track.trackName}</span>
                </td>
                <PaceCell ms={track.qualifyingMs} paceRef={track.qualifyingRef} driverId={driverId} navigate={navigate} />
                <PaceCell ms={track.heat1Ms} paceRef={track.heat1Ref} driverId={driverId} navigate={navigate} />
                <PaceCell ms={track.heat2Ms} paceRef={track.heat2Ref} driverId={driverId} navigate={navigate} />
                <PaceCell ms={track.finalMs} paceRef={track.finalRef} driverId={driverId} navigate={navigate} />
                <td className="py-3 px-4 text-center">
                  <span className="pill text-xs mono">
                    {track.eventCount}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PaceCell({ ms, paceRef, driverId, navigate }: { ms: number; paceRef?: PaceRef; driverId?: string; navigate?: (url: string) => void }) {
  if (!ms || ms <= 0) {
    return (
      <td className="py-3 px-4 text-right">
        <span className="text-text-muted">–</span>
      </td>
    );
  }

  const canNavigate = paceRef && driverId && navigate;
  const handleClick = canNavigate
    ? () => {
        const qp = new URLSearchParams();
        qp.set('type', paceRef.sessionType);
        qp.set('driver', driverId);
        navigate(`#/races/${paceRef.championshipId}/${paceRef.eventId}/${encodeURIComponent(paceRef.categoryName)}?${qp.toString()}`);
      }
    : undefined;

  return (
    <td className="py-3 px-3 text-right">
      <span
        className={`font-mono text-sm tabular-nums ${
          canNavigate
            ? 'text-accent hover:text-accent-hover cursor-pointer transition-colors'
            : 'text-text-primary'
        }`}
        onClick={handleClick}
      >
        {formatLapTime(ms)}
      </span>
    </td>
  );
}
